import React from 'react';
import Tooltip from '@/components/Tooltip';
import { fitTitle, layoutRadialLabel, RadialLabel } from '@/lib/chartText';

// Define a type for the segment
interface PieSegment {
  path: string;
  fill: string;
  label: RadialLabel;
  fillOpacity: number;
}

// Label typography, shared by both charts.
const LABEL_FONT_SIZE = 16;
const LABEL_LINE_HEIGHT = 18;

// A slice label, centred vertically on the point the layout chose for it.
function SegmentLabel({ label }: { label: RadialLabel }) {
  return (
    <text
      x={label.x}
      y={label.y}
      textAnchor={label.anchor}
      dominantBaseline="middle"
      className="text-[16px] font-mono md:text-[14px] sm:text-[12px]"
      fill="#333333"
    >
      {label.lines.map((line, i) => (
        <tspan key={i} x={label.x} dy={i === 0 ? label.lineOffsets[0] : LABEL_LINE_HEIGHT}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

export default function ComputeAllocation2024v2027() {
  // Chart dimensions
  const width = 1000;
  const height = 500;
  const margin = { top: 0, right: 240, bottom: 0, left: 100 };
  
  // Common values
  const centerY = height / 2;
  
  // 2024 chart positioning (left side) - smaller and more to the left
  const centerX2024 = width / 4.8;
  const radius2024 = Math.min(width / 2, height) / 6;
  const innerRadius2024 = radius2024 * 0.45;
  
  // 2027 chart positioning (right side)
  const centerX2027 = (width * 2.6) / 4;
  const radius2027 = Math.min(width / 2, height) / 3;
  const innerRadius2027 = radius2027 * 0.45;

  // Data for the 2024 pie chart
  const data2024 = [
    { label: "Esperimenti di ricerca", value: 4, tooltip: "Research experiments"},
    { label: " ", value: 1, tooltip: "Running AI assistants internally"},
    { label: "Addestramento", value: 40, tooltip: "Training"},
    { label: "Generazione di dati", value: 20 },
    { label: "Distribuzione\nesterna", value: 35 }, // Split with newline
  ];

  // Data for the 2027 pie chart
  const data2027 = [
    { label: "Esperimenti\ndi ricerca", value: 28 }, // Split with newline
    { label: "Esecuzione\ndi assistenti\nAI interni", value: 6 },
    { label: "Addestramento", value: 24 },
    { label: "Generazione\ndi dati", value: 22 },
    { label: "Distribuzione\nesterna", value: 20 } // Also split this for consistency
  ];

  // Create pie chart paths function
  const generatePieSegments = (
    data: Array<{label: string, value: number}>,
    centerX: number,
    chartRadius: number,
    chartInnerRadius: number,
    labelBounds: { min: number; max: number }
  ) => {
    let currentAngle = 0;
    const segments: PieSegment[] = [];
    
    // Calculate total for percentage calculations
    const total = data.reduce((sum, item) => sum + item.value, 0);

    // Sort data by value in descending order
    const sortedData = data//[...data].sort((a, b) => b.value - a.value);

    sortedData.forEach((item, index) => {
      // Calculate segment angles
      const angleSize = (item.value / total) * 2 * Math.PI;
      const startAngle = currentAngle - Math.PI / 2; // Start from the top (- PI/2)
      const endAngle = startAngle + angleSize;
      
      // Calculate outer arc coordinates
      const startX = centerX + Math.cos(startAngle) * chartRadius;
      const startY = centerY + Math.sin(startAngle) * chartRadius;
      const endX = centerX + Math.cos(endAngle) * chartRadius;
      const endY = centerY + Math.sin(endAngle) * chartRadius;
      
      // Calculate inner arc coordinates
      const innerStartX = centerX + Math.cos(startAngle) * chartInnerRadius;
      const innerStartY = centerY + Math.sin(startAngle) * chartInnerRadius;
      const innerEndX = centerX + Math.cos(endAngle) * chartInnerRadius;
      const innerEndY = centerY + Math.sin(endAngle) * chartInnerRadius;
      
      // Create path - move to outer start, arc to outer end, line to inner end, arc back to inner start, close
      const largeArcFlag = angleSize > Math.PI ? 1 : 0;
      const path = [
        `M ${startX} ${startY}`,
        `A ${chartRadius} ${chartRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        `L ${innerEndX} ${innerEndY}`,
        `A ${chartInnerRadius} ${chartInnerRadius} 0 ${largeArcFlag} 0 ${innerStartX} ${innerStartY}`,
        'Z'
      ].join(' ');
      
      // Place the label just outside the segment. Both the side it sits on and
      // the width it may occupy are derived from the geometry and from the
      // label's own length, so translated labels stay inside the chart.
      const label = layoutRadialLabel({
        centerX,
        centerY,
        angle: startAngle + (angleSize / 2),
        radius: chartRadius * 1.18,
        clearRadius: chartRadius,
        text: item.label,
        fontSize: LABEL_FONT_SIZE,
        lineHeight: LABEL_LINE_HEIGHT,
        bounds: labelBounds,
      });

      // Determine fill color by position in the data, which is the same in
      // every language: research experiments first, then internal assistants.
      let fill = "#333333"; // Default gray
      let fillOpacity = 0.9;

      if (index === 0) {
        // Largest slice - full accent color
        fill = "var(--accent)";
        fillOpacity = 0.9;
      } else if (index === 1) {
        // Second largest slice - accent color with lower opacity
        fill = "var(--accent)";
        fillOpacity = 0.6;
      } else {
        // Remaining slices - varying shades of gray
        const offset = 2;
        const grayValue = 20+(index-offset) * 80;
        fill = `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
        fillOpacity = 0.9;
      }

      segments.push({
        path,
        fill,
        label,
        fillOpacity
      });
      
      currentAngle += angleSize;
    });
    
    return segments;
  };

  // Horizontal room each chart's labels may use: out to the edge of the
  // drawing on the outside, and up to the neighbouring donut on the inside.
  const labelGutter = 24;
  const bounds2024 = { min: 4, max: centerX2027 - radius2027 - labelGutter };
  const bounds2027 = { min: centerX2024 + radius2024 + labelGutter, max: width - 4 };

  // Generate segments for both charts with their respective radii
  const segments2024 = generatePieSegments(data2024, centerX2024, radius2024, innerRadius2024, bounds2024);
  const segments2027 = generatePieSegments(data2027, centerX2027, radius2027, innerRadius2027, bounds2027);

  const title = "Allocazione della potenza di calcolo di OpenBrain, 2024 vs 2027";
  // The title has nowhere to wrap to, so an over-long translation is set at
  // a size that fits rather than running off the edge of the drawing.
  const titleStyle = fitTitle(title, width - 8);

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title - using the same style as before */}
        <text
          x={0}
          y={40}
          fontSize={titleStyle.fontSize}
          fontFamily="monospace"
          className={
            titleStyle.fits
              ? "text-[28px] font-mono md:text-[24px] sm:text-[20px]"
              : "font-mono"
          }
          fill="#333333"
        >
          {title}
        </text>
        <text
            x={centerX2024}
            y={centerY-5 +28}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="20"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            2024
          </text>
          
          <text
            x={centerX2027}
            y={centerY-4+28}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="30"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            2027
          </text>
          <text
            x={centerX2024}
            y={centerY+12+28}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="10"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            stima
          </text>
          
          <text
            x={centerX2027}
            y={centerY+20+28}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="14"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            proiezione
          </text>
        
        <g transform={`translate(0, 30)`}>
          {/* 2024 Pie segments */}
          {segments2024.map((segment, i) => (
            <g key={`segment-2024-${i}`}>
                  <path
                    d={segment.path}
                    fill={segment.fill}
                    fillOpacity={segment.fill === "var(--accent)" ? segment.fillOpacity : 0.9}
                    stroke="#ffffff"
                    strokeWidth=".5"
                  />
            </g>
          ))}
          
          {/* 2024 Labels */}
          {segments2024.map((segment, i) => (
            <SegmentLabel key={`label-2024-${i}`} label={segment.label} />
          ))}

          {/* 2027 Pie segments */}
          {segments2027.map((segment, i) => (
            <path
              key={`segment-2027-${i}`}
              d={segment.path}
              fill={segment.fill}
              fillOpacity={segment.fill === "var(--accent)" ? segment.fillOpacity : 0.9}
              stroke="#ffffff"
              strokeWidth=".5"
            />
          ))}
          
          {/* 2027 Labels */}
          {segments2027.map((segment, i) => (
            <SegmentLabel key={`label-2027-${i}`} label={segment.label} />
          ))}
        </g>
      </svg>
    </div>
  );
}